import { formatDate } from '../../../libs/date';
import { parseError } from '../../../libs/parser';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonDescription from '../../../components/CommonDescription';
import CommonIconButton from '../../../components/CommonIconButton';
import Context from '../../../provider';
import CustomerSelectComponent from '../../../components/CustomerSelectComponent';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import IconNews from '../../../assets/hkdc/icons/NewsIcon';
import IconNewsNone from '../../../assets/hkdc/icons/NoneNewsIcon';
import LogoFooterComponent from '../../../components/LogoFooterComponent';
import PageTitleContComponent from '../../../components/PageTitleContComponent';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import React, { useContext, useEffect, useRef, useState } from 'react';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

const sortOptions = [
  { label: 'default', value: 'default' },
  { label: 'last-updated', value: 'newest_to_oldest' }
];

export default function WebNews() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const scrollRef = useRef();
  const isScrolling = useIsScrolling(scrollRef.current);

  const notificationNotReadCount = useNotificationsCount();

  const { init, dispatch, lang, me } = useContext(Context);

  const [newsData, setNewsData] = useState([]);
  const [newsPageTotalCount, setNewsPageTotalCount] = useState(1);
  const [currentNewsPage, setCurrentNewsPage] = useState(1);
  const [newsSortOptions, setNewsSortOptions] = useState([]);
  const [newsSortType, setNewsSortType] = useState('default');

  const [pageData, setPageData] = useState(null);

  const fetchNewsData = async (page, sortType) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getNews({
        page,
        pageSize: 1000,
        sort: sortType
      });
      setNewsPageTotalCount(Math.ceil(response.list.length / 12));
      setNewsData((prevData) => [...prevData, ...(response?.list ?? []).filter((d) => !(prevData.find((p) => p.uri_name === d.uri_name)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchDescData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: 'news-header' });
      setPageData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  useEffect(() => {
    setNewsSortOptions(sortOptions);

    return () => {
      setNewsSortOptions([]);
    };
  }, []);

  useEffect(() => {
    fetchNewsData(1, 'default');
    fetchDescData();
  }, [init]);

  useEffect(() => {
    setNewsData([]);
    if (currentNewsPage > 1) {
      setCurrentNewsPage(1);
    } else {
      fetchNewsData(1, newsSortType);
    }
  }, [newsSortType]);

  return (
    (
      <div className='web-news-page'>
        <div ref={scrollRef} className="y-overflow gray-block content-container">
          <div className="black-block full-width">
            <PageTitleContComponent
              titleText={pageData?.sections[0].title[lang] ?? ''}
              descText={<CommonDescription data={pageData?.sections[0].content[lang] ?? ''} />}
              titleContStyle={{
                gap: '3rem'
              }}
              pageTitleIcon={
                <IconNews
                style={{
                  width: '23.75rem',
                  height: '22rem'
                }}
                size="large" />
              }
            />
          </div>

          <div className="section-max-container section">
            <div className="news-content-container">
              <div className="news-bar">
                <div className="bar-title-container">
                  <IconBenefitsRed
                  className="icon"
                  size="large" />
                  <div className='title'>
                    {t('common.news')}
                  </div>
                </div>
                <CustomerSelectComponent
                  title={t('news.sort')}
                  value={newsSortType}
                  options={newsSortOptions}
                  selectStyle={{
                    justifyContent: 'flex-end'
                  }}
                  selectChange={(value) => {
                    setNewsSortType(value);
                  }}
                />
              </div>

              {
                newsData.length > 0 &&
                <>
                  <div className="news-cont">
                    {
                      newsData
                      .filter((item, index) => index >= (currentNewsPage - 1) * 12 && index < currentNewsPage * 12 )
                      .map((item, index) => {
                        return (
                          <div key={index} className='item' onClick={() => {
                            if (item.external_url && item.external_url[lang]) {
                              window.location.assign(item.external_url[lang]);
                            } else {
                              navigate(`${config.basename}news-detail/${item.id}`, {
                                state: {
                                  id: item.id
                                }
                              });
                            }
                          }}>
                              <div className='item-img' style={{ backgroundImage: `url(${item.cover[lang]})` }} />
                              <div className='item-content'>
                                <div className="text-container">
                                  <div className='item-text-time'>
                                    {formatDate(item.updated_at, lang)}
                                  </div>
                                  <div className='item-text-title'>
                                    {item.title[lang]}
                                  </div>
                                </div>
                                <CommonIconButton
                                  text= {t('home-before.card-btn-4')}
                                  alignItems = "unset"
                                  backgroundColor= "#000000"
                                  btnBackgroundColor= "#000000"
                                  btnColor = "#ffffff"
                                  width= "max-content"
                                  padding = "0.5rem 0.75rem"
                                  btnPadding="0rem"
                                  btnFontSize = "1rem"
                                  btnFontWeight = "700"
                                  onClick={(e) => {
                                    if (item.external_url && item.external_url[lang]) {
                                      window.location.assign(item.external_url[lang]);
                                    } else {
                                      navigate(`${config.basename}news-detail/${item.id}`, {
                                        state: {
                                          id: item.id
                                        }
                                      });
                                    }
                                  }}
                                />
                              </div>
                          </div>
                        );
                      })
                    }
                  </div>

                  <div className="page-count">
                      <Pagination
                        count={newsPageTotalCount}
                        variant="outlined"
                        shape="rounded"
                        value={currentNewsPage}
                        onChange={(event, value) => {
                          setCurrentNewsPage(value);
                        }}
                        renderItem={(item) => {
                          return (
                            <PaginationItem
                            sx={{
                              borderRadius: '0',
                              border: '0.125rem solid #A1A3A5',
                              minWidth: '2.8125rem',
                              height: '2.8125rem',
                              padding: '0.25rem',
                              color: '#A1A3A5',
                              fontWeight: '700',
                              fontSize: '1rem',
                              '&.Mui-selected': {
                                border: '0.125rem solid #004899',
                                backgroundColor: '#004899',
                                color: 'white'
                              }
                            }}
                              {...item}
                            />
                          );
                        }}
                      />
                    </div>
                </>
              }
              {
                newsData.length === 0 &&
                <div className="none">
                  <div className="title-cont">
                    <IconNewsNone
                      className='icon'
                      size="large" />
                    <div className="title-text">
                      {t('none-data.all-news-title')}
                    </div>
                  </div>
                  <div className="desc">
                    {t('none-data.all-news-desc')}
                  </div>
                </div>
              }
            </div>
          </div>

          <div className="black-block full-width">
            <LogoFooterComponent />
          </div>
        </div>
      </div>
    )
  );
}